import React from 'react';
import PropTypes from 'prop-types';
import styled from 'react-emotion';
import classnames from 'classnames';

import checkedBg from '../assets/icon/duoxuan2_bs_icon@3x.png';
import DateInputModal from './date-input-modal';

const BaseInput = styled.input`
  &[type="number"]::-webkit-outer-spin-button,
  &[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
  }
  &[type="number"] {
      -moz-appearance: textfield;
  }
`;
export const WrappedInput = ({ input, ...props }) => (
  <BaseInput {...input} {...props} />
);

WrappedInput.defaultProps = {
  input: {}
};

WrappedInput.propTypes = {
  input: PropTypes.object, // redux-form input props
};

const Input = styled.div`
  flex-shrink: 0;

  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e6e6e6;
  background: white;
  height: 100px;

  &.compact {
    height: 85px;
  }
`;

Input.Label = styled.div`
  font-size: 26px;
  color: rgb(102, 102, 102);
  margin-left: 20px;
  line-height: 1;
  min-width: 202px;
`;

Input.Input = styled(WrappedInput)`
  background: none;
  flex-grow: 1;
  font-size: 28px;
  color: rgb(51, 51, 51);
  line-height: 1;
  /* cancel left padding on iOS */
  border: 1px solid transparent;
  padding-left: 0;
  &:focus, &:hover,
  &:active {
    outline: none !important;
  }
  text-overflow: ellipsis;
`;

Input.Box = styled.div`
  margin-right: 30px;
  width: 30px;
  height: 30px;
  border: 1px solid #999999;
`;

Input.CheckedBox = styled(Input.Box)`
  background-image: url(${checkedBg});
  background-size: cover;
`;

Input.Radio = styled.div`
  display: inline-block;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid #999999;
  vertical-align: middle;
`;

Input.ChosenRadio = styled(Input.Radio)`
  border: 2px solid #0991dc;
  background: #22a7f0;
  
  &.disabled {
    border: 2px solid #999999;
    background: #999;
  }
`;

Input.RadioLabel = styled.div`
  display: inline-block;
  font-size: 28px;
  color: rgb(134, 134, 134);
  margin: 0 80px 0 30px;
  vertical-align: middle;
  line-height: 1;
`;

Input.ChosenLabel = styled(Input.RadioLabel)`
  font-weight: 500;
  color: rgb(34, 167, 240);
  
  &.disabled {
    color: #999;
  }
`;

Input.More = styled.div`
  color: rgb(102, 102, 102);
  font-size: 24px;
  line-height: 1;
  margin-right: 20px;
`;

export const CheckBox = ({
  children, checked, compact, onClick
}) => (
  <Input className={classnames({ compact })} onClick={onClick}>
    <Input.Label>{children}</Input.Label>
    { checked ? <Input.CheckedBox /> : <Input.Box /> }
  </Input>
);

CheckBox.defaultProps = {
  checked: false,
  onClick: null,
  compact: false,
};

CheckBox.propTypes = {
  children: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  onClick: PropTypes.func,
  compact: PropTypes.bool,
};

export const Radio = ({
  label, compact, values, checkedIdx, onClick, disabled
}) => (
  <Input className={classnames({ compact })} onClick={onClick}>
    <Input.Label>{label}</Input.Label>
    <div style={{ flexGrow: '1', paddingLeft: '5px' }}>
      {
        values.map((v, idx) => (
          <span key={`s-${v}`} style={{ display: 'inline-block' }}>
            { checkedIdx === idx ?
              <Input.ChosenRadio
                className={classnames({ disabled })}
                key={`r-${v}`}
                onClick={() => !disabled && onClick(v, idx)} /> :
              <Input.Radio key={`r-${v}`} onClick={() => !disabled && onClick(v, idx)} /> }
            { checkedIdx === idx ?
              <Input.ChosenLabel
                className={classnames({ disabled })}
                key={v}
                onClick={
                () => !disabled && onClick(v, idx)
              }>{v}
              </Input.ChosenLabel> :
              <Input.RadioLabel key={v}
                onClick={
                () => !disabled && onClick(v, idx)
              }>{v}
              </Input.RadioLabel>
            }
          </span>
        ))
      }
    </div>

  </Input>
);

Radio.defaultProps = {
  checkedIdx: null,
  onClick: () => null,
  compact: false,
  disabled: false,
};

Radio.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  label: PropTypes.string.isRequired,
  checkedIdx: PropTypes.number,
  onClick: PropTypes.func,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
};

export const InlineInput = ({
  children, label, compact, onClick
}) => (
  <Input className={classnames({ compact })} onClick={onClick}>
    <Input.Label>{label}</Input.Label>
    {children}
  </Input>
);

InlineInput.defaultProps = {
  compact: false,
  onClick: () => null,
};

InlineInput.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  compact: PropTypes.bool,
  onClick: PropTypes.func,
};

InlineInput.Input = Input.Input;

InlineInput.More = Input.More;

InlineInput.DateField = (props) => {
  const {
    input: { value, onChange },
    disabled,
    setState,
    lastDate,
    dateModalOpen,
  } = props;

  const date = value || '1980-1-1';

  const [y, m, d] = date.split('-');
  const displayDate = value ? `${y}年${m}月${d}日` : '';

  const openPicker = () => {
    if (disabled) return;
    setState({ dateModalOpen: true });
  };

  return (
    <InlineInput label='出生年月' onClick={openPicker} >
      <InlineInput.Input type='text' value={displayDate} readOnly />
      <InlineInput.More>
        {value && value.length ? ' >' : '请选择 >'}
      </InlineInput.More>
      <DateInputModal
        value={date}
        onSubmit={() => setState({
          dateModalOpen: false,
          lastDate: date,
        }, () => onChange(date))
        }
        onCancel={() => setState({
          dateModalOpen: false,
        }, () => onChange(lastDate))
        }
        onChange={onChange}
        isOpen={dateModalOpen} />
    </InlineInput>
  );
};

InlineInput.DateField.defaultProps = {
  lastDate: null,
  dateModalOpen: false,
  disabled: false,
};

InlineInput.DateField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  setState: PropTypes.func.isRequired,
  lastDate: PropTypes.string,
  dateModalOpen: PropTypes.bool,
  disabled: PropTypes.bool,
};

InlineInput.RadioField = (props) => {
  const {
    input: { onChange, value, ...input },
    meta,
    disabled,
  } = props;
  return (
    <Radio
      label='性别'
      checkedIdx={value ? value - 1 : value}
      values={['男士', '女士']}
      disabled={disabled}
      onClick={(v, idx) => onChange(idx >= 0 ? idx + 1 : idx)}
      {...input}
      meta={meta}
    />
  );
};

InlineInput.RadioField.defaultProps = {
  disabled: false,
};

InlineInput.RadioField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  meta: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/inline-input.jsx